import type { AxiosInstance } from 'axios';
import type { AddonType, SubscriptionAddon, SubscriptionFeatures } from '../../types/Subscription';

// Types for subscription management
export interface SubscriptionData {
  stripeCustomerId?: string;
  subscriptionId?: string;
  planId: string;
  priceId?: string;
  status: 'active' | 'canceled' | 'past_due' | 'trialing' | 'incomplete' | 'incomplete_expired' | 'unpaid';
  currentPeriodEnd: number;
  trialEndsAt?: number;
  cancelAtPeriodEnd?: boolean;
  canceledAt?: number;
  access: string[];
  addons?: SubscriptionAddon[];
  features?: SubscriptionFeatures;
}

export interface CurrentSubscriptionResponse {
  hasSubscription: boolean;
  subscription?: SubscriptionData;
}

export interface PortalSessionRequest {
  returnUrl: string;
}

export interface PortalSessionResponse {
  portalUrl: string;
}

export interface CheckoutSessionRequest {
  priceIds: string[];
  successUrl: string;
  cancelUrl: string;
  mode?: 'payment' | 'subscription';
  trialDays?: number;
  allowPromotionCodes?: boolean;
  metadata?: Record<string, string>;
}

export interface AddAddonRequest {
  addonPriceId: string;
}

export interface AddAddonResponse {
  success: boolean;
  addons: SubscriptionAddon[];
  features: SubscriptionFeatures;
}

export interface RemoveAddonRequest {
  addonType: AddonType;
}

export interface RemoveAddonResponse {
  success: boolean;
  addons: SubscriptionAddon[];
  features: SubscriptionFeatures;
}

export interface PreviewAddonChangeRequest {
  addPriceIds?: string[];
  removeAddonTypes?: string[];
}

export interface PreviewAddonChangeResponse {
  amountDue: number;
  currency: string;
  prorationDate: number;
  lines: ProrationLine[];
}

export interface CheckoutSessionResponse {
  success: boolean;
  checkoutUrl: string;
}

export interface ChangeSubscriptionRequest {
  priceId: string;
  prorationBehavior?: 'create_prorations' | 'none' | 'always_invoice';
}

export interface ChangeSubscriptionResponse {
  success: boolean;
  subscription: SubscriptionData;
}

export interface PreviewChangeRequest {
  priceId: string;
}

export interface ProrationLine {
  amount: number;
  description: string;
  period: {
    start: number;
    end: number;
  };
}

export interface PreviewChangeResponse {
  amountDue: number;
  currency: string;
  prorationDate: number;
  lines: ProrationLine[];
}

export interface CancelSubscriptionRequest {
  cancelAtPeriodEnd?: boolean;
  reason?: 'too_expensive' | 'too_complex' | 'missing_features' | 'other';
  feedback?: string;
}

export interface CancelSubscriptionResponse {
  success: boolean;
  subscription: {
    id: string;
    status: string;
    currentPeriodEnd: number;
    cancelAtPeriodEnd: boolean;
    canceledAt?: number;
  };
}

export interface ReactivateSubscriptionResponse {
  success: boolean;
  subscription: SubscriptionData;
}

export interface Invoice {
  id: string;
  number?: string;
  amountPaid: number;
  amountDue: number;
  total: number;
  currency: string;
  status: 'draft' | 'open' | 'paid' | 'void' | 'uncollectible';
  created: number;
  periodStart: number;
  periodEnd: number;
  pdfUrl?: string;
  hostedInvoiceUrl?: string;
}

export interface InvoiceDetails extends Invoice {
  lines: Array<{
    amount: number;
    description: string;
    period: {
      start: number;
      end: number;
    };
  }>;
}

export interface InvoiceListResponse {
  invoices: Invoice[];
  hasMore: boolean;
}

export interface ProductPrice {
  id: string;
  unitAmount: number | null;
  currency: string;
  interval: 'month' | 'year' | null;
  intervalCount: number | null;
}

export interface Product {
  id: string;
  name: string;
  description: string | null;
  metadata: Record<string, string>;
  marketingFeatures: Array<{
    name: string;
  }> | null;
  prices: ProductPrice[];
}

export interface ProductsResponse {
  products: Product[];
}

export const SubscriptionService = (api: AxiosInstance) => ({
  // Get current subscription status
  getCurrentSubscription: async (): Promise<CurrentSubscriptionResponse> => {
    try {
      const response = await api.get<CurrentSubscriptionResponse>('/subscriptions/current');
      return response.data;
    } catch (error) {
      console.error('Error getting current subscription:', error);
      throw error;
    }
  },

  // Create Stripe Customer Portal session
  createPortalSession: async (returnUrl: string): Promise<string> => {
    try {
      const response = await api.post<PortalSessionResponse>('/subscriptions/portal-session', {
        returnUrl
      });
      return response.data.portalUrl;
    } catch (error) {
      console.error('Error creating portal session:', error);
      throw error;
    }
  },

  // Create Stripe Checkout session for new subscription
  createCheckoutSession: async (params: CheckoutSessionRequest): Promise<string> => {
    try {
      const response = await api.post<CheckoutSessionResponse>('/subscriptions/checkout-session', params);
      return response.data.checkoutUrl;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      throw error;
    }
  },

  // Change subscription plan
  changeSubscription: async (priceId: string, prorationBehavior?: string): Promise<SubscriptionData> => {
    try {
      const response = await api.post<ChangeSubscriptionResponse>('/subscriptions/change', {
        priceId,
        prorationBehavior
      });
      return response.data.subscription;
    } catch (error) {
      console.error('Error changing subscription:', error);
      throw error;
    }
  },

  // Preview subscription change (proration calculation)
  previewSubscriptionChange: async (priceId: string): Promise<PreviewChangeResponse> => {
    try {
      const response = await api.post<PreviewChangeResponse>('/subscriptions/preview-change', {
        priceId
      });
      return response.data;
    } catch (error) {
      console.error('Error previewing subscription change:', error);
      throw error;
    }
  },

  // Cancel subscription
  cancelSubscription: async (params: CancelSubscriptionRequest = {}): Promise<CancelSubscriptionResponse> => {
    try {
      const response = await api.post<CancelSubscriptionResponse>('/subscriptions/cancel', params);
      return response.data;
    } catch (error) {
      console.error('Error canceling subscription:', error);
      throw error;
    }
  },

  // Reactivate canceled subscription
  reactivateSubscription: async (): Promise<SubscriptionData> => {
    try {
      const response = await api.post<ReactivateSubscriptionResponse>('/subscriptions/reactivate');
      return response.data.subscription;
    } catch (error) {
      console.error('Error reactivating subscription:', error);
      throw error;
    }
  },

  // List invoices
  listInvoices: async (limit?: number, startingAfter?: string): Promise<InvoiceListResponse> => {
    try {
      const params = new URLSearchParams();
      if (limit) params.append('limit', limit.toString());
      if (startingAfter) params.append('startingAfter', startingAfter);

      const response = await api.get<InvoiceListResponse>(`/subscriptions/invoices?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error listing invoices:', error);
      throw error;
    }
  },

  // Get invoice details
  getInvoice: async (invoiceId: string): Promise<InvoiceDetails> => {
    try {
      const response = await api.get<InvoiceDetails>(`/subscriptions/invoices/${invoiceId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting invoice:', error);
      throw error;
    }
  },

  // List available products
  listProducts: async (): Promise<ProductsResponse> => {
    try {
      const response = await api.get<ProductsResponse>('/subscriptions/products');
      return response.data;
    } catch (error) {
      console.error('Error listing products:', error);
      throw error;
    }
  },

  // Add addon to subscription
  addAddon: async (addonPriceId: string): Promise<AddAddonResponse> => {
    try {
      const response = await api.post<AddAddonResponse>('/subscriptions/addons', {
        addonPriceId
      });
      return response.data;
    } catch (error) {
      console.error('Error adding addon:', error);
      throw error;
    }
  },

  // Remove addon from subscription
  removeAddon: async (addonType: AddonType): Promise<RemoveAddonResponse> => {
    try {
      const response = await api.post<RemoveAddonResponse>('/subscriptions/addons/remove', {
        addonType
      });
      return response.data;
    } catch (error) {
      console.error('Error removing addon:', error);
      throw error;
    }
  },

  // Preview addon change
  previewAddonChange: async (params: PreviewAddonChangeRequest): Promise<PreviewAddonChangeResponse> => {
    try {
      const response = await api.post<PreviewAddonChangeResponse>('/subscriptions/addons/preview', params);
      return response.data;
    } catch (error) {
      console.error('Error previewing addon change:', error);
      throw error;
    }
  }
});